import { useMemo, useState } from 'react'
import { where } from 'firebase/firestore'
import { useCollection } from '../../lib/firestore'
import { dayKey, formatDayLong, formatTime } from '../../lib/format'
import type { ItineraryItem, Show, Tour, WithId } from '../../types/models'
import Icon, { itineraryIcon } from '../../components/Icon'
import DateScroller from '../../components/DateScroller'
import type { DayEntry } from '../../components/DateScroller'
import Empty from '../../components/Empty'

/**
 * A tour's itinerary, one day at a time.
 *
 * Shared by the Itinerary tab and the tour's own Schedule page so the two can't
 * drift — they're the same view of the same data reached two ways, and having
 * built two of them once already is how the Schedule route ended up rendering
 * the desktop layout on a phone.
 */
export default function DayItinerary({
  tour,
  onOpenItem,
  emptyAction,
}: {
  tour: WithId<Tour>
  onOpenItem?: (item: WithId<ItineraryItem>) => void
  emptyAction?: { label: string; onClick: () => void }
}) {
  const items = useCollection<ItineraryItem>('itineraryItems', [where('tourId', '==', tour.id)])
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const { days, byDay, showCities } = useMemo(() => {
    const byDay = new Map<string, WithId<ItineraryItem>[]>()
    for (const item of items.data) {
      // Grouped by the item's OWN timezone day — a Melbourne arrival must not
      // slide onto the next day for a viewer in New Zealand.
      const key = dayKey(item.timeUTC.toDate(), item.timezone)
      const bucket = byDay.get(key)
      if (bucket) bucket.push(item)
      else byDay.set(key, [item])
    }
    for (const bucket of byDay.values()) {
      bucket.sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())
    }

    const showCities = new Map<string, string>()
    for (const show of shows.data) {
      showCities.set(dayKey(show.date.toDate(), show.timezone), show.city)
    }

    const days: DayEntry[] = Array.from(byDay.keys())
      .sort()
      .map((key) => ({
        key,
        date: dateFromKey(key),
        city: showCities.get(key) ?? null,
        count: byDay.get(key)?.length ?? 0,
      }))

    return { days, byDay, showCities }
  }, [items.data, shows.data])

  // Opens on today, or the next day with anything on it.
  const resolvedKey = useMemo(() => {
    if (selectedKey) return selectedKey
    if (days.length === 0) return null
    const todayKey = dayKey(new Date())
    return (days.find((day) => day.key >= todayKey) ?? days[0]).key
  }, [selectedKey, days])

  const dayItems = resolvedKey ? (byDay.get(resolvedKey) ?? []) : []
  const city = resolvedKey ? showCities.get(resolvedKey) : null

  if (!items.loading && items.data.length === 0) {
    return (
      <Empty
        title="Nothing scheduled"
        message="Add flights, hotels and shows and they'll build the itinerary."
        action={emptyAction}
      />
    )
  }

  return (
    <>
      <DateScroller days={days} selectedKey={resolvedKey} onSelect={setSelectedKey} />

      {resolvedKey && (
        <div className="stack" style={{ gap: 1 }}>
          {city ? (
            <>
              <span className="t-display-m">{city}</span>
              <span className="t-spec-caption dim">
                {formatDayLong(dateFromKey(resolvedKey))}
              </span>
            </>
          ) : (
            <span className="t-display-s">{formatDayLong(dateFromKey(resolvedKey))}</span>
          )}
        </div>
      )}

      {dayItems.length === 0 ? (
        <p className="panel panel-pad t-caption">Nothing scheduled on this day.</p>
      ) : (
        <div className="panel rows">
          {dayItems.map((item) => {
            const content = (
              <>
                <span
                  className="t-timecode"
                  style={{
                    minWidth: 50,
                    color: item.needsTime ? 'var(--text-tertiary)' : undefined,
                  }}
                >
                  {item.needsTime ? '--:--' : formatTime(item.timeUTC, item.timezone)}
                </span>
                <Icon
                  name={itineraryIcon(item.type)}
                  size={16}
                  style={{ color: 'var(--text-secondary)' }}
                />
                <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                  <span className="truncate">{item.title}</span>
                  {item.subtitle && <span className="t-caption truncate">{item.subtitle}</span>}
                </span>
                {item.needsTime && <span className="chip chip-accent">Set time</span>}
              </>
            )

            if (!onOpenItem) {
              return (
                <div key={item.id} className="row" style={{ padding: '14px 16px', gap: 12 }}>
                  {content}
                </div>
              )
            }

            return (
              <button
                key={item.id}
                onClick={() => onOpenItem(item)}
                className="row"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  gap: 12,
                  textAlign: 'left',
                }}
              >
                {content}
              </button>
            )
          })}
        </div>
      )}
    </>
  )
}

function dateFromKey(key: string): Date {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year, month - 1, day)
}
